/**
 * Карта ликвидности: где рынку есть за чем ходить.
 *
 * Уровень отмечается там, где остались чужие заявки и стопы. Источников
 * три, и они про разное:
 *
 * **Аномально объёмная свеча.** Набирали позицию — значит заявки стоят
 * не в середине свечи, а за её краями. Такая свеча оставляет два уровня:
 * за максимумом и за минимумом.
 *
 * **Экстремум.** За хаем и лоу стоят стопы, и именно за ними рынок
 * обычно и ходит. Сила уровня берётся по объёму той свечи, на которой
 * экстремум сформировался: хай на всплеске держит больше заявок.
 *
 * **Экстремум старшего таймфрейма.** Образуется редко, живёт долго и
 * держит несравнимо больше заявок.
 *
 * Уровень живёт с момента образования и обрывается там, где цена его
 * прошла: снятая ликвидность больше не ликвидность. Снятый не исчезает,
 * а гаснет — по контрасту сразу видно, где ещё есть что забрать.
 *
 * Перенесено из пайна «Выгодные зоны PRO» шаг в шаг. Расхождение с ним
 * было бы худшим исходом: человек смотрит на два экрана сразу и поверит
 * ошибающемуся ровно с той же вероятностью, что и правому.
 */
import { atr, sma } from './math.js';
import { swings } from './structure.js';
/*
 * Настройки, которых в терминале не видно.
 *
 * Это не отобранная у человека свобода, а состав индикатора. Открыв их,
 * мы отдали бы не удобство настройки, а сам рецепт: подобранные пороги и
 * есть то, чем эта карта отличается от любой другой. Наружу вынесено то,
 * что меняет вид, а не смысл.
 */
const SPIKE_MULT = 1.8;
const VOLUME_WINDOW = 50;
const BAND_ATR = 0.5;
const ATR_LENGTH = 14;
const PIVOT_BARS = 3;
const MTF_POWER = 2.2;
/** Часовой и четырёхчасовой, в миллисекундах. */
const MTF_STEPS = [3_600_000, 4 * 3_600_000];
/** Дальше этого в прошлое полоса не тянется. */
const MAX_LOOKBACK_BARS = 4000;
/**
 * Шаг ряда по времени.
 *
 * Медиана, а не разность последних двух. В ряду бывают разрывы — биржа
 * стояла, свечи нет. Разность через такой разрыв дала бы шаг в несколько
 * часов, и всё, что от него считается, уехало бы.
 */
export function stepOf(candles) {
    if (candles.length < 2)
        return 0;
    const deltas = [];
    for (let i = 1; i < candles.length; i += 1) {
        const delta = candles[i].time - candles[i - 1].time;
        if (delta > 0)
            deltas.push(delta);
    }
    if (deltas.length === 0)
        return 0;
    deltas.sort((a, b) => a - b);
    return deltas[Math.floor(deltas.length / 2)];
}
/**
 * Свечи, пересобранные в старший таймфрейм.
 *
 * Строятся из того же ряда, а не запрашиваются отдельно: терминалу
 * незачем ходить в Core ради карты, а собрать час из пятнадцатиминуток
 * можно точно. Обратное неверно — час из дня не собрать, и такой
 * таймфрейм просто пропускается.
 */
export function resample(candles, step) {
    const out = [];
    for (const candle of candles) {
        const bucket = Math.floor(candle.time / step) * step;
        const last = out[out.length - 1];
        if (!last || last.time !== bucket) {
            out.push({ ...candle, time: bucket });
            continue;
        }
        out[out.length - 1] = {
            time: bucket,
            open: last.open,
            high: Math.max(last.high, candle.high),
            low: Math.min(last.low, candle.low),
            close: candle.close,
            volume: last.volume + candle.volume,
        };
    }
    return out;
}
/** Экстремумы старших таймфреймов как события во времени. */
function mtfEvents(candles, step) {
    const events = [];
    if (step <= 0)
        return events;
    MTF_STEPS.forEach((mtfStep, order) => {
        // Свой таймфрейм и младшие пропускаются: собрать их не из чего, а
        // повторить текущий значило бы удвоить те же уровни.
        if (mtfStep <= step)
            return;
        const bars = resample(candles, mtfStep);
        if (bars.length === 0)
            return;
        const pivots = swings(bars, PIVOT_BARS, PIVOT_BARS);
        // Второй таймфрейм тяжелее первого: его экстремумы ещё реже.
        const power = MTF_POWER + order * 0.5;
        for (let i = 1; i < bars.length; i += 1) {
            /*
             * Полоса начинается там, где мы об уровне узнали, а не там, где
             * экстремум случился. Точное место на старшем таймфрейме отсюда не
             * восстановить, и рисовать наугад хуже, чем рисовать честно.
             */
            const known = bars[i].time + mtfStep;
            const high = pivots.high[i];
            const low = pivots.low[i];
            if (pivots.highAt[i] !== null && pivots.highAt[i] !== pivots.highAt[i - 1] && high != null) {
                events.push({ time: known, price: high, power, side: 'above' });
            }
            if (pivots.lowAt[i] !== null && pivots.lowAt[i] !== pivots.lowAt[i - 1] && low != null) {
                events.push({ time: known, price: low, power, side: 'below' });
            }
        }
    });
    return events.sort((a, b) => a.time - b.time);
}
/** Уровни карты по ряду свечей. */
export function liquidityMap(candles, options) {
    if (candles.length === 0)
        return [];
    const limit = Math.max(1, Math.floor(options.levels));
    const age = Math.max(1, Math.floor(options.age));
    const volumes = candles.map((candle) => candle.volume);
    const average = sma(volumes, VOLUME_WINDOW);
    const local = swings(candles, PIVOT_BARS, PIVOT_BARS);
    const step = stepOf(candles);
    const events = mtfEvents(candles, step);
    let nextEvent = 0;
    /** Действующая карта: то, что видел бы человек на этом баре. */
    const levels = [];
    /**
     * Всё, что когда-либо на карте стояло.
     *
     * Вытесненный уровень не исчезает из расчёта — он перестаёт быть
     * частью карты, и только с этого бара. Выбрось мы его совсем, прошлое
     * оценивалось бы по карте, которой тогда не было.
     */
    const history = [];
    const push = (level, at) => {
        levels.push(level);
        history.push(level);
        /*
         * Вытеснение съедает сначала снятые, и только если снятых не
         * осталось — самый старый живой. Иначе лимит уходит на историю, а
         * действующие уровни в дальней части графика пропадают — притом что
         * нужны как раз они.
         */
        while (levels.length > limit) {
            const used = levels.findIndex((entry) => entry.toIndex !== null);
            const [gone] = levels.splice(used === -1 ? 0 : used, 1);
            if (gone)
                gone.evictedIndex = at;
        }
    };
    for (let i = 0; i < candles.length; i += 1) {
        const candle = candles[i];
        const avg = average[i];
        // Источник первый: объёмная свеча оставляет два уровня — за краями,
        // а не в середине.
        if (avg != null && avg > 0 && candle.volume >= avg * SPIKE_MULT) {
            const power = candle.volume / avg;
            push({ price: candle.high, fromIndex: i, toIndex: null, evictedIndex: null, power, side: 'above' }, i);
            push({ price: candle.low, fromIndex: i, toIndex: null, evictedIndex: null, power, side: 'below' }, i);
        }
        // Источник второй: подтверждённые экстремумы этого таймфрейма.
        const highAt = local.highAt[i];
        const high = local.high[i];
        if (highAt != null && highAt !== local.highAt[i - 1] && high != null) {
            push({
                price: high,
                fromIndex: highAt,
                toIndex: null,
                evictedIndex: null,
                power: powerAt(candles, average, highAt),
                side: 'above',
            }, i);
        }
        const lowAt = local.lowAt[i];
        const low = local.low[i];
        if (lowAt != null && lowAt !== local.lowAt[i - 1] && low != null) {
            push({
                price: low,
                fromIndex: lowAt,
                toIndex: null,
                evictedIndex: null,
                power: powerAt(candles, average, lowAt),
                side: 'below',
            }, i);
        }
        // Источник третий: экстремумы старших таймфреймов — в тот бар, на
        // котором о них стало известно.
        while (nextEvent < events.length && events[nextEvent].time <= candle.time) {
            const event = events[nextEvent];
            nextEvent += 1;
            push({
                price: event.price,
                fromIndex: i,
                toIndex: null,
                evictedIndex: null,
                power: event.power,
                side: event.side,
            }, i);
        }
        /*
         * Снятие: цена дошла до уровня и забрала его. Свеча образования не
         * считается — иначе уровень умирал бы в момент рождения.
         */
        for (let k = levels.length - 1; k >= 0; k -= 1) {
            const level = levels[k];
            if (level.toIndex === null &&
                i > level.fromIndex &&
                candle.low <= level.price &&
                candle.high >= level.price) {
                level.toIndex = i;
            }
            // Уровень, до которого цена так и не дошла, со временем перестаёт
            // что-либо значить.
            if (i - level.fromIndex > age) {
                level.evictedIndex = i;
                levels.splice(k, 1);
            }
        }
    }
    /*
     * Отдаётся вся история карты, а не её последнее состояние. Кто рисует
     * — берёт живые (`evictedAt === null`); кто считает прошлое — смотрит,
     * что стояло на нужном баре.
     */
    return history.map((level) => ({
        price: level.price,
        from: candles[level.fromIndex].time,
        to: level.toIndex === null ? null : candles[level.toIndex].time,
        evictedAt: level.evictedIndex === null ? null : candles[level.evictedIndex].time,
        power: level.power,
        side: level.side,
    }));
}
/** Сила уровня по объёму той свечи, на которой он образовался. */
function powerAt(candles, average, index) {
    const avg = average[index];
    const candle = candles[index];
    if (!candle || avg == null || avg <= 0)
        return 1;
    return candle.volume / avg;
}
/** Полутолщина полосы: сила читается длиной и цветом, а не высотой. */
export function halfHeightOf(atrValue, power) {
    return (atrValue * BAND_ATR * (0.7 + Math.min(power, 4) * 0.08)) / 2;
}
/** ATR последнего бара: толщина у всех полос одна и та же, как в пайне. */
export function lastAtr(candles) {
    const series = atr(candles.map((candle) => candle.high), candles.map((candle) => candle.low), candles.map((candle) => candle.close), ATR_LENGTH);
    return series[series.length - 1] ?? 0;
}
/**
 * Насыщенность: слабые уровни едва проступают, сильные читаются сразу,
 * снятые дополнительно гаснут.
 */
export function alphaOf(power, alive) {
    const transparency = Math.min(95, Math.max(20, Math.round(88 - Math.min(power, 4) * 15) + (alive ? 0 : 22)));
    return (100 - transparency) / 100;
}
/** Насколько далеко в прошлое имеет смысл тянуть полосу. */
export function earliest(candles, step) {
    const last = candles[candles.length - 1];
    return last ? last.time - MAX_LOOKBACK_BARS * step : 0;
}
//# sourceMappingURL=liquidity.js.map